import copy from 'copy-to-clipboard'
import * as React from 'react'
import { MqDesktop, MqTablet } from 'streamr-ui/css/consts'
import styled from 'styled-components'
import { Section, SectionContent, SectionDescription, SectionTitle } from '../../Section'
import Binance from '../assets/binance.png'
import Binance2x from '../assets/binance@2x.png'
import MetaMask from '../assets/metamask.png'
import MetaMask2x from '../assets/metamask@2x.png'
import Tokens from '../assets/tokens.png'
import Tokens2x from '../assets/tokens@2x.png'

export function GetData() {
    return (
        <GetDataRoot id="get-data">
            <SectionContent>
                <SectionTitle>How to get DATA</SectionTitle>
                <SectionDescription>
                    The DATA token exists on Ethereum, Polygon POS, Gnosis chain and the Binance
                    smart chain. The Staking rewards and the Streamr Ecosystem are currently on the
                    Polygon POS&nbsp;chain.
                </SectionDescription>
                <StepList>
                    <Step>
                        <StepImage
                            alt="Choose an exchange"
                            src={Binance}
                            srcSet={`${Binance2x} 2x`}
                        />
                        <StepTitle>
                            <StepNumber>1</StepNumber>
                            <StepName>Choose an exchange</StepName>
                        </StepTitle>
                        <StepDesc>
                            Get DATA on popular cryptocurrency exchanges. Platforms like Binance and
                            Uniswap offer trading pairs with DATA, making it easy to exchange with
                            other cryptocurrencies.
                        </StepDesc>
                    </Step>
                    <Step>
                        <StepImage
                            alt="Wallet Integration"
                            src={MetaMask}
                            srcSet={`${MetaMask2x} 2x`}
                        />
                        <StepTitle>
                            <StepNumber>2</StepNumber>
                            <StepName>Wallet Integration</StepName>
                        </StepTitle>
                        <StepDesc>
                            Store your DATA securely in compatible crypto wallets. Popular options
                            include MetaMask, Trust Wallet, and Ledger. Ensure your wallet supports
                            ERC-20 tokens to manage your DATA seamlessly.
                        </StepDesc>
                    </Step>
                    <Step>
                        <StepImage alt="Staking rewards" src={Tokens} srcSet={`${Tokens2x} 2x`} />
                        <StepTitle>
                            <StepNumber>3</StepNumber>
                            <StepName>Staking rewards</StepName>
                        </StepTitle>
                        <StepDesc>
                            Earn DATA tokens by contributing to the Streamr Network. By
                            participating in the staking process, you not only contribute to network
                            security and efficiency but also receive DATA tokens as incentives.
                        </StepDesc>
                    </Step>
                </StepList>
                <Resources>
                    <ResourceList>
                        <ResourceListTitle>DATA token contracts</ResourceListTitle>
                        <AddressResourceListItem
                            address="0x8f693ca8D21b157107184d29D398A8D082b38b76"
                            label="Ethereum"
                        />
                        <AddressResourceListItem
                            address="0x3a9A81d576d83FF21f26f325066054540720fC34"
                            label="Polygon POS"
                        />
                        <AddressResourceListItem
                            address="0x256eb8a51f382650B2A1e946b8811953640ee47D"
                            label="Gnosis Chain"
                        />
                        <AddressResourceListItem
                            address="0x0864c156b3C5F69824564dEC60c629aE6401bf2a"
                            label="Binance BSC"
                        />
                    </ResourceList>
                    <ResourceList>
                        <ResourceListTitle>DATA Bridges</ResourceListTitle>
                        <UrlResourceListItem
                            url="https://portal.polygon.technology"
                            label="Polygon POS"
                        />
                        <UrlResourceListItem
                            url="https://bridge.gnosischain.com"
                            label="Gnosis Chain"
                        />
                        <UrlResourceListItem
                            url="https://bnbchain.org/en/bnb-chain-bridge"
                            label="Binance BSC"
                        />
                    </ResourceList>
                </Resources>
            </SectionContent>
        </GetDataRoot>
    )
}

function UrlResourceListItem({ label, url }) {
    return (
        <ResourceListItem as="a" href={url} target="_blank" rel="noopener noreferrer">
            <ResourceListItemLabel>{label}</ResourceListItemLabel>
            <ResourceListItemContent>
                <ResourceListItemValue>{url.replace(/^https?:\/\//, '')}</ResourceListItemValue>
                <ResourceListItemCta>{ExternalLinkIcon}</ResourceListItemCta>
            </ResourceListItemContent>
        </ResourceListItem>
    )
}

function AddressResourceListItem({ label, address }) {
    const [copied, setCopied] = React.useState(false)

    const timeoutIdRef = React.useRef()

    React.useEffect(() => {
        let mounted = true

        if (!copied) {
            return
        }

        if (timeoutIdRef.current != null) {
            clearTimeout(timeoutIdRef.current)

            timeoutIdRef.current = undefined
        }

        timeoutIdRef.current = setTimeout(() => {
            if (!mounted) {
                return
            }

            setCopied(false)
        }, 3000)

        return () => {
            mounted = false
        }
    }, [copied])

    return (
        <ResourceListItem
            onClick={() => {
                copy(address, {
                    onCopy: () => {
                        setCopied(true)
                    },
                })
            }}
        >
            <ResourceListItemLabel>{label}</ResourceListItemLabel>
            <ResourceListItemContent>
                <ResourceListItemValue>{address}</ResourceListItemValue>
                <ResourceListItemCtaWrap>
                    {copied && (
                        <AddressCopiedBadge>
                            <svg
                                width="10"
                                height="8"
                                viewBox="0 0 10 8"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M1.09375 4.56222L3.47425 7.13146L9.1311 1.47461"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <span>Address Copied!</span>
                        </AddressCopiedBadge>
                    )}
                    <ResourceListItemCta>{CopyIcon}</ResourceListItemCta>
                </ResourceListItemCtaWrap>
            </ResourceListItemContent>
        </ResourceListItem>
    )
}

const AddressCopiedBadge = styled.div`
    align-items: center;
    display: flex;
    gap: 10px;
    height: 32px;
    position: absolute;
    transform: translateX(-50%) translateY(-100%) translateY(-12px);
    left: 50%;
    white-space: nowrap;
    font-size: 14px;
    line-height: 28px;
    background: rgba(250, 250, 250, 0.11);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    padding: 0 14px;

    svg {
        flex: none;
    }
`

const CopyIcon = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.3758 2.92578H8.12578C7.15238 2.92578 6.36328 3.71488 6.36328 4.68828V5.11328H5.93828C4.96488 5.11328 4.17578 5.90238 4.17578 6.87578V15.0008C4.17578 15.9742 4.96488 16.7633 5.93828 16.7633H12.1883C13.1617 16.7633 13.9508 15.9742 13.9508 15.0008V14.5758H14.3758C15.3492 14.5758 16.1383 13.7867 16.1383 12.8133V4.68828C16.1383 3.71488 15.3492 2.92578 14.3758 2.92578ZM12.1883 16.5633C13.0512 16.5633 13.7508 15.8637 13.7508 15.0008V14.3758H14.3758C15.2387 14.3758 15.9383 13.6762 15.9383 12.8133V4.68828C15.9383 4.25669 15.7633 3.86597 15.4804 3.58319C15.7628 3.8659 15.9375 4.2563 15.9375 4.6875V12.8125C15.9375 13.6754 15.2379 14.375 14.375 14.375H13.75V15C13.75 15.8629 13.0504 16.5625 12.1875 16.5625H5.9375C5.5063 16.5625 5.1159 16.3878 4.83319 16.1054C5.11596 16.3883 5.50669 16.5633 5.93828 16.5633H12.1883ZM8.12578 14.5758H12.6133V15.0008C12.6133 15.2355 12.423 15.4258 12.1883 15.4258H5.93828C5.70356 15.4258 5.51328 15.2355 5.51328 15.0008V6.87578C5.51328 6.64106 5.70356 6.45078 5.93828 6.45078H6.36328V12.8133C6.36328 13.7867 7.15238 14.5758 8.12578 14.5758ZM5.31328 6.87578C5.31328 6.5306 5.5931 6.25078 5.93828 6.25078H6.5625V6.25H5.9375C5.59232 6.25 5.3125 6.52982 5.3125 6.875V15C5.3125 15.1728 5.38264 15.3293 5.49602 15.4424C5.3831 15.3293 5.31328 15.1732 5.31328 15.0008V6.87578ZM12.8125 14.3758H8.12578C7.6942 14.3758 7.30348 14.2008 7.02071 13.9179C7.30342 14.2003 7.69381 14.375 8.125 14.375H12.8125V14.3758ZM7.70078 4.68828C7.70078 4.45356 7.89106 4.26328 8.12578 4.26328H14.3758C14.6105 4.26328 14.8008 4.45356 14.8008 4.68828V12.8133C14.8008 13.048 14.6105 13.2383 14.3758 13.2383H8.12578C7.89106 13.2383 7.70078 13.048 7.70078 12.8133V4.68828ZM7.50078 4.68828C7.50078 4.3431 7.7806 4.06328 8.12578 4.06328H14.3758C14.5482 4.06328 14.7043 4.1331 14.8174 4.24602C14.7043 4.13264 14.5478 4.0625 14.375 4.0625H8.125C7.77982 4.0625 7.5 4.34232 7.5 4.6875V12.8125C7.5 12.9853 7.57014 13.1418 7.68352 13.2549C7.5706 13.1418 7.50078 12.9857 7.50078 12.8133V4.68828Z"
            fill="currentColor"
        />
    </svg>
)

const ExternalLinkIcon = (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.5 10.7188C12.5 10.3045 12.8358 9.96875 13.25 9.96875H21.25C21.6642 9.96875 22 10.3045 22 10.7188V18.7188C22 19.133 21.6642 19.4688 21.25 19.4688C20.8358 19.4688 20.5 19.133 20.5 18.7188V11.4688H13.25C12.8358 11.4688 12.5 11.133 12.5 10.7188Z"
            fill="currentColor"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.7803 10.1884C22.0732 10.4813 22.0732 10.9562 21.7803 11.2491L10.7803 22.2491C10.4874 22.542 10.0126 22.542 9.71967 22.2491C9.42678 21.9562 9.42678 21.4813 9.71967 21.1884L20.7197 10.1884C21.0126 9.89553 21.4874 9.89553 21.7803 10.1884Z"
            fill="currentColor"
        />
    </svg>
)

const ResourceListItemLabel = styled.div`
    font-weight: 500;
`

const ResourceListItemValue = styled.div`
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const ResourceListItemContent = styled.div`
    display: flex;
    margin-top: 6px;
    gap: 12px;
`

const ResourceListItemCtaWrap = styled.div`
    flex: none;
    width: 28px;
    height: 28px;
    position: relative;
`

const ResourceListItemCta = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background-color: rgba(255, 255, 255, 0.04);
    border-radius: 4px;
    flex: none;
    transition: 400ms background-color;
`

const ResourceListItem = styled.button`
    appearance: none;
    border: 0;
    text-align: left;
    color: inherit;
    display: block;
    width: 100%;
    box-sizing: border-box;
    font-size: 16px;
    line-height: 24px;
    padding: 16px 20px;
    gap: 6px;
    background: rgba(255, 255, 255, 0.03);
    mix-blend-mode: normal;
    backdrop-filter: blur(100px);
    border-radius: 8px;
    transition: 400ms background-color;

    &:active {
        transform: translateY(1px);
    }

    a& {
        text-decoration: none !important;
    }

    & + & {
        margin-top: 16px;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        transition-duration: 50ms;
    }

    &:hover ${ResourceListItemCta} {
        background-color: #ffffff;
        transition-duration: 50ms;
        color: #191d29;
    }
`

const ResourceListTitle = styled.div`
    font-weight: 500;
    font-size: 20px;
    line-height: 32px;
    margin-bottom: 24px;
`

const ResourceList = styled.div`
    min-width: 0;
`

const Resources = styled.div`
    display: grid;
    gap: 40px;
    margin-top: 116px;

    @media ${MqTablet} {
        grid-template-columns: 1fr 1fr;
    }

    @media ${MqDesktop} {
        gap: 80px;
    }
`

const StepImage = styled.img`
    display: block;
    margin-bottom: 40px;
    width: 64px;
    height: 64px;

    @media ${MqTablet} {
        margin-bottom: 56px;
        width: 80px;
        height: 80px;
    }

    @media ${MqDesktop} {
        margin-bottom: 72px;
        width: 92px;
        height: 92px;
    }
`

const StepDesc = styled.div`
    font-size: 16px;
    line-height: 26px;
`

const StepNumber = styled.div`
    background: #0324ff;
    border-radius: 50%;
    font-weight: 600;
    font-size: 16px;
    line-height: 30px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
`

const StepName = styled.div`
    font-weight: 500;
    font-size: 20px;
    line-height: 32px;
`

const StepTitle = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 24px;
`

const Step = styled.div`
    background: rgba(255, 255, 255, 0.03);
    mix-blend-mode: normal;
    backdrop-filter: blur(100px);
    border-radius: 16px;
    padding: 44px 36px;
    box-sizing: border-box;
`

const StepList = styled.div`
    display: grid;
    gap: 20px;
    margin: 72px auto 0;
    width: 420px;
    max-width: 100%;

    @media ${MqTablet} {
        width: auto;
        margin-top: 96px;
        grid-template-columns: 1fr 1fr;
    }

    @media ${MqDesktop} {
        gap: 28px;
        grid-template-columns: 1fr 1fr 1fr;
        margin-top: 116px;
    }
`

const GetDataRoot = styled(Section)`
    background: #121622;
    background-image: radial-gradient(
        circle at 30% 15%,
        rgba(62, 79, 236, 0.3) 0,
        transparent 50vw
    );
    color: #ffffff;
    padding-top: var(--defaultPadding) !important;

    ${SectionTitle},
    ${SectionDescription} {
        text-align: center;
    }

    ${SectionDescription} {
        margin: 0 auto;
        width: 780px;
    }
`
